import type { FileClient } from '../files/client'
import { createAbortError, throwIfAborted } from '../shared/abort'
import type { XmaxErrorCode } from '../shared/sdk-error'
import {
  OFFLINE_TASK_SUPPORTED_FPS,
  type OfflineMediaInput,
  type OfflineRequestOptions,
  type OfflineSubmitOptions,
} from './types'

export type ThrowOfflineError = (
  code: XmaxErrorCode,
  messageKey: string,
  params?: Record<string, string | number>,
) => never

export function resolvePositiveNumber(
  value: number | undefined,
  fallback: number,
  label: string,
  throwOfflineError: ThrowOfflineError,
): number {
  const resolved = value ?? fallback
  if (!Number.isFinite(resolved) || resolved <= 0) {
    throwOfflineError('INVALID_INPUT', 'errors.offlinePositiveNumber', { label })
  }
  return resolved
}

export function normalizeUid(
  taskUid: string,
  throwOfflineError: ThrowOfflineError,
): string {
  const normalized = taskUid.trim()
  if (!normalized) {
    throwOfflineError('INVALID_INPUT', 'errors.offlineTaskUidEmpty')
  }
  return normalized
}

function normalizeUploadedUrl(
  value: string,
  label: string,
  throwOfflineError: ThrowOfflineError,
): string {
  const normalized = value.trim()
  if (!/^https?:\/\//i.test(normalized)) {
    throwOfflineError(
      'INVALID_INPUT',
      'errors.offlineUploadedUrlRequired',
      { label },
    )
  }
  return normalized
}

export function validateSubmitOptions(
  options: OfflineSubmitOptions,
  throwOfflineError: ThrowOfflineError,
): void {
  if (options.quality !== undefined && options.quality !== 'sd' && options.quality !== 'hd') {
    throwOfflineError('INVALID_INPUT', 'errors.offlineQualityInvalid')
  }
  if (
    options.fps !== undefined
    && !(OFFLINE_TASK_SUPPORTED_FPS as readonly number[]).includes(options.fps)
  ) {
    throwOfflineError('INVALID_INPUT', 'errors.offlineFpsUnsupported')
  }
}

export async function resolveSourceUrl(
  files: FileClient,
  input: OfflineMediaInput,
  options: OfflineRequestOptions | undefined,
  throwOfflineError: ThrowOfflineError,
): Promise<string> {
  return typeof input === 'string'
    ? normalizeUploadedUrl(input, 'source', throwOfflineError)
    : (options
        ? (await files.uploadVideo(input, options)).url
        : (await files.uploadVideo(input)).url)
}

export async function resolveReferenceImageUrl(
  files: FileClient,
  input: OfflineMediaInput | null | undefined,
  options: OfflineRequestOptions | undefined,
  throwOfflineError: ThrowOfflineError,
): Promise<string | undefined> {
  if (input === null || input === undefined) return undefined
  return typeof input === 'string'
    ? normalizeUploadedUrl(input, 'context.refImageUrl', throwOfflineError)
    : (options
        ? (await files.uploadImage(input, options)).url
        : (await files.uploadImage(input)).url)
}

export function waitForPollDelay(ms: number, signal: AbortSignal): Promise<void> {
  throwIfAborted(signal)
  return new Promise((resolve, reject) => {
    const timeoutId = globalThis.setTimeout(() => {
      signal.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    const onAbort = () => {
      globalThis.clearTimeout(timeoutId)
      signal.removeEventListener('abort', onAbort)
      reject(createAbortError())
    }
    signal.addEventListener('abort', onAbort, { once: true })
  })
}

export function linkAbortSignal(
  source: AbortSignal | undefined,
  target: AbortController,
): () => void {
  if (!source) return () => {}
  const onAbort = () => target.abort()
  if (source.aborted) {
    target.abort()
    return () => {}
  }
  source.addEventListener('abort', onAbort, { once: true })
  return () => source.removeEventListener('abort', onAbort)
}
